import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Lock, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import './AuthPages.scss';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);

  const validatePassword = (password: string): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    if (password.length < 8) errors.push('mažiausiai 8 simboliai');
    if (!/[A-Z]/.test(password)) errors.push('bent 1 didžioji raidė');
    if (!/[a-z]/.test(password)) errors.push('bent 1 mažoji raidė');
    if (!/\d/.test(password)) errors.push('bent 1 skaičius');
    return { valid: errors.length === 0, errors };
  };

  const getPasswordStrength = (): number => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    return strength;
  };

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setTokenValid(false);
        return;
      }

      try {
        await axios.get(`${API_URL}/auth/verify-reset-token/${token}`);
        setTokenValid(true);
      } catch (err) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Slaptažodis privalomas');
      return;
    }

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.valid) {
      setError(`Slaptažodis turi turėti: ${passwordValidation.errors.join(', ')}`);
      return;
    }

    if (password !== confirmPassword) {
      setError('Slaptažodžiai nesutampa');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/reset-password`, { token, password });
      setSuccess(true);
    } catch (err: any) {
      const message = err.response?.data?.message || 'Klaida keičiant slaptažodį. Bandykite dar kartą.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="auth-card">
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Tikrinama nuoroda...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!token || !tokenValid) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="breadcrumbs">
            <Link to="/" className="breadcrumb-link">Pradžia</Link>
            <ChevronRight size={14} className="separator" />
            <span className="current">Slaptažodžio atstatymas</span>
          </div>

          <div className="auth-card">
            <div className="error-state">
              <div className="error-icon">
                <XCircle size={64} />
              </div>
              <h1>Negaliojanti nuoroda</h1>
              <p>
                Ši slaptažodžio atstatymo nuoroda nebegalioja arba jau buvo panaudota.
              </p>
              <div className="error-actions">
                <Link to="/forgot-password" className="btn-primary">
                  Prašyti naujos nuorodos
                </Link>
                <Link to="/login" className="btn-secondary">
                  Grįžti į prisijungimą
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-container">
          <div className="breadcrumbs">
            <Link to="/" className="breadcrumb-link">Pradžia</Link>
            <ChevronRight size={14} className="separator" />
            <span className="current">Slaptažodžio atstatymas</span>
          </div>

          <div className="auth-card">
            <div className="success-state">
              <div className="success-icon">
                <CheckCircle size={64} />
              </div>
              <h1>Slaptažodis pakeistas!</h1>
              <p>
                Jūsų slaptažodis buvo sėkmingai pakeistas.
              </p>
              <p className="hint">
                Dabar galite prisijungti su nauju slaptažodžiu.
              </p>
              <div className="success-actions">
                <Link to="/login" className="btn-primary">
                  Prisijungti
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const passwordStrength = getPasswordStrength();
  const passwordValidation = validatePassword(password);

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="breadcrumbs">
          <Link to="/" className="breadcrumb-link">Pradžia</Link>
          <ChevronRight size={14} className="separator" />
          <span className="current">Naujas slaptažodis</span>
        </div>

        <div className="auth-card">
          <div className="auth-header">
            <div className="header-icon">
              <Lock size={32} />
            </div>
            <h1>Naujas slaptažodis</h1>
            <p>Įveskite naują slaptažodį savo paskyrai.</p>
          </div>

          {error && (
            <div className="error-alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            <div className="form-group">
              <label htmlFor="password">Naujas slaptažodis</label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError('');
                  }}
                  className={error ? 'error' : ''}
                  placeholder="Įveskite naują slaptažodį"
                  autoComplete="new-password"
                  autoFocus
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {password && (
                <>
                  <div className="password-strength">
                    <div className="strength-bar">
                      <div 
                        className={`strength-fill strength-${passwordStrength}`}
                        style={{ width: `${(passwordStrength / 4) * 100}%` }}
                      ></div>
                    </div>
                    <span className={`strength-text strength-${passwordStrength}`}>
                      {passwordStrength === 0 && 'Labai silpnas'}
                      {passwordStrength === 1 && 'Silpnas'}
                      {passwordStrength === 2 && 'Vidutinis'}
                      {passwordStrength === 3 && 'Stiprus'}
                      {passwordStrength === 4 && 'Labai stiprus'}
                    </span>
                  </div>
                  <div className="password-requirements">
                    <span className={password.length >= 8 ? 'requirement-met' : ''}>
                      {password.length >= 8 ? '✓' : '○'} 8+ simboliai
                    </span>
                    <span className={/[A-Z]/.test(password) ? 'requirement-met' : ''}>
                      {/[A-Z]/.test(password) ? '✓' : '○'} Didžioji raidė
                    </span>
                    <span className={/[a-z]/.test(password) ? 'requirement-met' : ''}>
                      {/[a-z]/.test(password) ? '✓' : '○'} Mažoji raidė
                    </span>
                    <span className={/\d/.test(password) ? 'requirement-met' : ''}>
                      {/\d/.test(password) ? '✓' : '○'} Skaičius
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Pakartokite slaptažodį</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (error) setError('');
                  }}
                  className={confirmPassword && password !== confirmPassword ? 'error' : ''}
                  placeholder="Pakartokite naują slaptažodį"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {confirmPassword && password !== confirmPassword && (
                <span className="field-error">Slaptažodžiai nesutampa</span>
              )}
            </div>

            <button 
              type="submit" 
              className="submit-btn" 
              disabled={loading || !passwordValidation.valid || password !== confirmPassword}
            >
              {loading ? 'Keičiama...' : 'Pakeisti slaptažodį'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
